import React, {MouseEvent, useCallback, useEffect, useRef, useState} from 'react';
import cx from 'classnames';
import {observer} from 'mobx-react';
import {noop} from '@priority-software/priority-style-react';
import {isNavigationLocked} from '../utils/isNavigationLocked';
import {formsStore} from '../../../stores/Form/FormsStore';
import {Form as FormType} from '../../../stores/Form';
import {Grid} from './Grid/Grid';
import {listTypes} from './SelectListModal/enum';
import {FormToolbar} from './FormToolbar';
import {FormView} from './FormView';
import {codes, keys} from '../../../constants/KeyCodes';
import {closeFormBySession, updateFormView} from '../actions/form';
import {eventPrevented} from '../../../utils/eventPrevented';
import {chooseCloseZoomForm} from '../actions/zoom';
import {runSiblingSubform} from '../utils/grid-methods';
import {subFormNavDirections} from '../consts/grid';
import {clearFormData, clearOneRow, runFiltersFromForm, runSearchMode} from '../actions/filters';
import {executeSearch} from '../actions/searches';
import {CollapsedFormView} from './CollapsedFormView';
import {FormViewEnum} from '../../../stores/Form/Form';
import {formatEventForMacro} from '../../Macro/utils/macroEventFormats';
import {showFormHelp} from '../../../_shared/Help/actions/helpActions';
import {HelpBook} from '../../HelpBook';
import {isActiveForm} from '../utils/activeForm';
import {cx_menu_input_anchor} from './FormInput/constants';
import {useContextMenu} from './ContextMenu/hooks/useContextMenu';
import {showFormFieldHelp} from '../utils/help';
import {pasteToColumn, isExcelClipboardData, pasteToCellF10} from '../utils/pasteToColumn';
import {useF9Dialog} from '../../../hooks/F9Dialog/useF9Dialog';
import {deleteRecord} from '../actions/delete';
import {FormDesignPanel} from '../../FormDesign/components/FormDesignPanel';
import {createDataTestId} from '../../../utils/dataTestId';
import {undoAction} from '../actions/undo';
import {createTemporarilyRecord} from '../../../stores/Form/utils';
import {fetchFirstPage, fetchLastPage} from '../actions/pagination';
import {PAGE_LIMIT} from '../consts/pagination';
import {requestPaginationData} from '../actions/pagination';
import {formF12handler} from '../utils/formShortcuts';
import {mayDesignForm} from '../utils/designUtils';
import {useContextMenuData} from './ContextMenu/hooks/useContextMenuData';
import styles from '../styles/form.module.scss';

type Props = {
    currentForm: FormType;
    isApiLock: () => boolean;
    isFormLocked?: boolean; // The special prop to disable the form interaction, i.e. when a RecordDialog dialog is open on the screen
    isSubForm?: boolean;
    openSelectListModal: (listType: listTypes) => void;
    componentClasses?: string;
    openNewEmptyRecord?: () => void;
    hideNewEmptyRecord?: () => void;
    isEmptyForm?: boolean;
};

export const Form = observer(
    ({componentClasses, openNewEmptyRecord, isFormLocked, isEmptyForm, ...restProps}: Props) => {
        const {currentForm, isSubForm, isApiLock: isLock, hideNewEmptyRecord} = restProps;
        const anchorElement = useRef<HTMLElement>(null);
        const tableWrapper = useRef<HTMLDivElement>(null);
        const isActive = isActiveForm(currentForm) && !isFormLocked;
        const currentRecord = currentForm.records[currentForm.currentRow];
        const activeKeys = currentRecord?.keys;
        const viewType = currentForm.view;
        const isApiLock = isLock();

        const callHelpOnField = () => {
            const {currentColumn} = formsStore.getActiveForm();
            showFormFieldHelp({anchor: anchorElement.current, column: currentColumn});
            closeContextMenu();
        };

        const EditModeFunc = () => {
            const {tableAPI, currentColumn} = formsStore.currentForm;
            currentColumn.setEditMode(true);
            currentColumn.setMaxWidthMode(true);
            tableAPI.recalcColumnStyles();
            closeContextMenu();
        };

        const openFormDesignPanel = () => {
            setIsDesignOpen(true);
            closeContextMenu();
        };

        const contextMenuData = useContextMenuData({
            helpFieldFunc: callHelpOnField,
            moveEditFunc: EditModeFunc,
            openFormDesignFunc: openFormDesignPanel,
            clearAllFunc: () => clearFormData(currentForm),
            hideRecordFunc: () => clearOneRow(currentForm)
        });

        const {openContextMenu, closeContextMenu, contextMenuComponent} = useContextMenu({
            menuData: contextMenuData
        });

        const {F9Dialog, isOpen} = useF9Dialog({});
        const [isDesignOpen, setIsDesignOpen] = useState(false);

        useEffect(
            function handleShortCodes() {
                if (isActive) {
                    if (currentForm.collapsed) {
                        // Expand form view when the form becomes active again, i.e. after clicking ESC from child form

                        currentForm.toggleViewState(false);
                    }

                    const handleKeyDown = async e => {
                        formsStore.setEventForRecordingMacro(formatEventForMacro({e}));
                        const ctrlKey = e.ctrlKey;
                        const isMultiLineForm = !currentForm.form.OneLine;

                        if (
                            e.repeat ||
                            isApiLock ||
                            isNavigationLocked(currentForm) ||
                            formsStore.currentScreen?.isNavigationBlocked
                        )
                            return;

                        switch (e.key) {
                            case keys.HOME: {
                                if (ctrlKey && isMultiLineForm) {
                                    e.preventDefault();
                                    await fetchFirstPage(currentForm, 0);
                                }
                                break;
                            }
                            case keys.END: {
                                if (ctrlKey && isMultiLineForm) {
                                    e.preventDefault();
                                    // Change to the last non-empty row in the page
                                    await fetchLastPage(
                                        currentForm,
                                        (currentForm.allRecordsCount % PAGE_LIMIT) - 1
                                    );
                                }
                                break;
                            }

                            case keys.F1: {
                                if (ctrlKey) {
                                    showFormHelp();
                                }
                                break;
                            }
                            case keys.F7: {
                                if (!formsStore.currentScreen?.isRecordDialog) {
                                    if (ctrlKey) {
                                        await clearFormData(currentForm);
                                        break;
                                    }
                                    await clearOneRow(currentForm);
                                }
                                break;
                            }
                            case keys.F9: {
                                // if (ctrlKey) {
                                //     openF9Dialog(true);
                                // } else {
                                //     openF9Dialog(false);
                                // }
                                break;
                            }
                            case keys.F10: {
                                pasteToCellF10(currentForm);
                                break;
                            }
                            case keys.ESC:
                                if (isSubForm) {
                                    await formsStore.currentScreen.handleFormLevel(
                                        currentForm.parentForm?.level
                                    );
                                } else {
                                    const currentScreen = formsStore.currentScreen;

                                    if (!currentScreen.isRecordDialog) {
                                        await closeFormBySession(
                                            currentForm.session,
                                            !currentScreen.zoomSource
                                        );
                                    }
                                }
                                break;

                            case keys.ENTER:
                                e.stopPropagation();
                                if (currentForm.searchModeLevel) {
                                    await runFiltersFromForm(
                                        currentForm,
                                        currentForm.searchModeLevel
                                    );
                                    break;
                                }

                                if (ctrlKey) {
                                    createTemporarilyRecord(currentForm);
                                    return;
                                }

                                break;

                            case keys.F12:
                                // Go to subform
                                formF12handler(e, currentForm);
                                break;

                            case keys.F11:
                                eventPrevented(e, true);
                                if (!formsStore.currentScreen?.isRecordDialog && isMultiLineForm) {
                                    if (e.shiftKey) {
                                        currentForm.searchStore.openSearch();
                                    } else if (e.ctrlKey) {
                                        await requestPaginationData(
                                            currentForm,
                                            PAGE_LIMIT,
                                            currentForm.paginationOffset,
                                            currentForm.currentRow
                                        );
                                    } else {
                                        runSearchMode(currentForm, currentForm.searchModeLevel);
                                    }
                                }
                                break;

                            case keys.F8:
                                const currentScreen = formsStore.currentScreen;

                                if (currentScreen.zoomSource) {
                                    await chooseCloseZoomForm(currentScreen);
                                } else if (!isSubForm) {
                                    await closeFormBySession(currentForm.session);
                                }
                                break;

                            case keys.F4:
                                if (isMultiLineForm) {
                                    const nextFormView =
                                        currentForm.view === FormViewEnum.table
                                            ? FormViewEnum.form
                                            : FormViewEnum.table;
                                    await updateFormView(currentForm, nextFormView);
                                }
                                break;

                            case keys.ARROW_RIGHT:
                                if (e.altKey && isSubForm) {
                                    eventPrevented(e, true);
                                    await runSiblingSubform(
                                        subFormNavDirections.right,
                                        currentForm
                                    );
                                }
                                break;

                            case keys.ARROW_LEFT:
                                if (e.altKey && isSubForm) {
                                    eventPrevented(e, true);
                                    await runSiblingSubform(subFormNavDirections.left, currentForm);
                                }
                                break;
                            case keys.DELETE:
                                if (ctrlKey) {
                                    eventPrevented(e, true);
                                    await deleteRecord(currentForm, hideNewEmptyRecord);
                                }
                                break;
                            default:
                                break;
                        }

                        if (ctrlKey && e.code === codes.B && mayDesignForm()) {
                            e.preventDefault();
                            setIsDesignOpen(true);
                        } else if (ctrlKey && e.code === codes.Z) {
                            e.preventDefault();
                            await undoAction();
                        }

                        const runDefaultSearch = e.key === '0';
                        if (e.key >= '0' && e.key <= '9' && ctrlKey) {
                            e.preventDefault();
                            const savedSearches = currentForm?.searchStore.savedSearches || [];
                            const searchToExecute = savedSearches.find(search => {
                                if (runDefaultSearch) {
                                    return search.isDefault;
                                }
                                return search.index === Number(e.key);
                            });

                            if (searchToExecute) {
                                executeSearch(currentForm, searchToExecute);
                            }
                        }
                    };

                    const handleMouseClick = e => {
                        formsStore.setEventForRecordingMacro(formatEventForMacro({e}));
                    };

                    const onPaste = e => {
                        // Use native paste if clipboard doesn't include excel like characters
                        if (!isExcelClipboardData(e)) return;

                        // TODO: handle query mode case

                        e.preventDefault();
                        pasteToColumn(e);
                    };
                    const copyRef = tableWrapper.current;
                    window.addEventListener('keydown', handleKeyDown);
                    window.addEventListener('click', handleMouseClick);
                    copyRef.addEventListener('paste', onPaste);
                    return () => {
                        window.removeEventListener('keydown', handleKeyDown);
                        window.removeEventListener('click', handleMouseClick);
                        copyRef.removeEventListener('paste', onPaste);
                    };
                }
                return noop;
            },
            [isActive, isSubForm, isApiLock, activeKeys, currentForm, hideNewEmptyRecord]
        );

        const viewRender = () => {
            if (currentForm.collapsed) {
                return <CollapsedFormView currentForm={currentForm} />;
            } else if (
                viewType === FormViewEnum.table &&
                (!formsStore.currentScreen?.isRecordDialog || isSubForm)
            ) {
                return (
                    <Grid
                        {...restProps}
                        isActiveForm={isActive}
                        defaultHeight={currentForm.tableHeight}
                        isEmptyForm={isEmptyForm}
                        openNewEmptyRecord={openNewEmptyRecord}
                    />
                );
            }
            return <FormView {...restProps} isActiveForm={isActive} />;
        };

        const handleContext = async (e: MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            e.preventDefault();

            if (isApiLock) {
                return;
            }
            const target = e.target as HTMLDivElement;
            const anchor: HTMLElement = target.closest(`.${cx_menu_input_anchor}`);

            if (isActive) {
                // Dataset name -> column name
                if (anchor?.dataset?.name) {
                    const column = currentForm.getColumnByName(anchor.dataset.name);
                    const rowIndex = Number(anchor.dataset.rowidx);
                    if (rowIndex >= 0) {
                        currentForm.setCurrentRow(Number(anchor.dataset.rowidx));
                    }
                    currentForm.setCurrentColumnId(column.id);
                    anchorElement.current = anchor;
                    openContextMenu(anchor, e.clientX, e.clientY);
                }
            } else {
                const {currentScreen} = formsStore;
                await currentScreen.handleFormLevel(currentForm.level);
            }
        };

        const {title} = currentForm;

        const {
            helpPageQueue,
            isLoadingHelpData,
            addHelpPage,
            setHelpPageQuery,
            setHelpActiveListPage,
            removeHelpPage,
            formHelp,
            showHelp
        } = currentForm.helpBook;

        const onHelpBookClose = useCallback(() => {
            currentForm.helpBook.setShowHelp(false);
            currentForm.helpBook.initHelpPageQueue();
        }, [currentForm.helpBook]);

        return (
            <>
                {!currentForm.collapsed ? (
                    <div
                        className={cx(componentClasses)}
                        {...createDataTestId('form-collapsed-container', false)}
                    >
                        <FormToolbar
                            isFormActive={isActive}
                            openRecordDialog={openNewEmptyRecord}
                            currentForm={restProps.currentForm}
                            hideNewEmptyRecord={hideNewEmptyRecord}
                        />
                    </div>
                ) : null}

                <div
                    {...createDataTestId('form-container', false)}
                    ref={tableWrapper}
                    className={cx(styles.component, componentClasses)}
                    onContextMenu={handleContext}
                    onKeyDown={closeContextMenu}
                >
                    {viewRender()}
                    {contextMenuComponent}
                    {isOpen && F9Dialog}
                    <HelpBook
                        helpPageQueue={helpPageQueue}
                        onClose={onHelpBookClose}
                        title={title}
                        isLoadingHelpData={isLoadingHelpData}
                        addHelpPage={addHelpPage}
                        setHelpPageQuery={setHelpPageQuery}
                        setHelpActiveListPage={setHelpActiveListPage}
                        removeHelpPage={removeHelpPage}
                        formHelp={formHelp}
                        showHelp={showHelp}
                    />
                    {isDesignOpen && <FormDesignPanel onClose={() => setIsDesignOpen(false)} />}
                </div>
            </>
        );
    }
);
